import React, { useEffect, useState } from "react";
import { BarChart3, Box, Loader2, PackageX, TrendingUp } from "lucide-react";
import type { ClosedPositionData } from "@crypto-trading/common/browser";
import { MetricCard } from "@/components/ui/MetricCard";
import { EmptyState } from "@/components/ui/EmptyState";
import {
  ClosedPositionCards,
  ClosedPositionsTable,
  Pager,
} from "@/components/ClosedPositionsList";
import {
  ExchangeFilterSelect,
  type ExchangeFilterOption,
} from "@/components/ExchangeFilterSelect";
import { useStats } from "@/hooks/useStats";
import { useClosedPositionsPage } from "@/hooks/usePositions";

const usd = (value: number) =>
  `${value < 0 ? "-" : ""}$${Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/** Total Opened · Total Closed · Position Volume · Total PnL. */
export const StatisticsCards: React.FC<{ userId: string; mobile?: boolean }> = ({ userId, mobile }) => {
  const { data: stats } = useStats(userId);
  const pnl = stats?.totalPnl ?? 0;
  const size = mobile ? "sm" : "lg";
  return (
    <div className={mobile ? "grid grid-cols-2 gap-2" : "grid grid-cols-4 gap-4"}>
      <MetricCard size={size} tone="green" icon={<Box />} label="Total Opened" value={stats?.totalOpenedPositions ?? 0} />
      <MetricCard size={size} tone="red" icon={<PackageX />} label="Total Closed" value={stats?.totalClosedPositions ?? 0} />
      <MetricCard size={size} tone="purple" icon={<BarChart3 />} label="Position Volume" value={usd(stats?.totalUsdAmount ?? 0)} />
      <MetricCard
        size={size}
        tone="blue"
        icon={<TrendingUp />}
        label="Total PnL"
        value={<span className={pnl < 0 ? "text-[#f87171]" : pnl > 0 ? "text-[#2dcc89]" : ""}>{usd(pnl)}</span>}
      />
    </div>
  );
};

/** "Closed Positions (n)" card: exchange filter, table or cards, pages. */
export const ClosedPositionsPanel: React.FC<{
  userId: string;
  mobile?: boolean;
  onSelect: (position: ClosedPositionData) => void;
}> = ({ userId, mobile, onSelect }) => {
  const perPage = mobile ? 5 : 10;
  const [exchange, setExchange] = useState<ExchangeFilterOption>("all");
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [exchange]);
  const { data, isLoading } = useClosedPositionsPage(
    userId,
    perPage,
    page,
    exchange === "all" ? undefined : exchange
  );
  const positions = data?.positions ?? [];
  const total = data?.pagination.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / perPage));

  return (
    <section className="glass flex flex-col !rounded-2xl">
      <div className={`flex gap-3 ${mobile ? "flex-col items-start p-4" : "items-center justify-between px-6 py-5"}`}>
        <h2 className="text-base font-semibold text-white">Closed Positions ({total})</h2>
        <div className="flex items-center gap-3">
          {!mobile && <span className="text-sm font-medium text-[#a3a3a3]">Exchange</span>}
          <ExchangeFilterSelect value={exchange} onChange={setExchange} className={mobile ? "w-[180px]" : "w-[200px]"} />
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="size-8 animate-spin text-accent" />
        </div>
      )}

      {!isLoading && positions.length === 0 && (
        <div className="border-t border-white/10">
          <EmptyState
            compact={mobile}
            image="/empty-stats.png"
            title="No Closed Positions Found"
            description="Your closed trades will appear here once you start trading."
          />
        </div>
      )}

      {positions.length > 0 &&
        (mobile ? (
          <div className="border-t border-white/10 p-3">
            <ClosedPositionCards positions={positions} offset={(page - 1) * perPage} onSelect={onSelect} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <ClosedPositionsTable positions={positions} offset={(page - 1) * perPage} onSelect={onSelect} />
          </div>
        ))}

      {positions.length > 0 && pageCount > 1 && (
        <div className={`border-t border-white/10 ${mobile ? "p-3" : "px-6 py-4"}`}>
          <Pager page={page} pageCount={pageCount} onChange={setPage} compact={mobile} />
        </div>
      )}
    </section>
  );
};
